import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  CalendarCheck,
  ChevronDown,
  Gift,
  HandHeart,
  Heart,
  MapPin,
  Package,
  ShieldCheck,
  Truck,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ProductCard } from "@/components/ProductCard";
import { Faq } from "@/components/Faq";
import { products, DEMO_IMAGES } from "@/data/products";
import { ReviewsCarousel } from "@/components/ReviewsCarousel";
import { DeliveryMarquee } from "@/components/DeliveryMarquee";

const Hero = () => {
    const { t } = useLang();
    return (
        <section
            data-testid="hero-section"
            className="relative min-h-[90svh] sm:min-h-[86svh] flex flex-col overflow-hidden"
        >
            <img
                src={DEMO_IMAGES.hero}
                alt="Osito personalizado, ramo de rosas y caja sorpresa con globos corazón frente a la puesta de sol de Marbella"
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#3d0a14]/95 via-[#3d0a14]/35 to-transparent" />
            <div className="relative mt-auto w-full">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-14 pb-9 sm:pb-16 lg:pb-20 text-white text-left">
                    <div
                        data-testid="hero-delivery-badge"
                        className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-[#3d0a14]/55 px-3.5 py-1.5 mb-4"
                    >
                        <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400"></span>
                        </span>
                        <span className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.12em]">
                            {t("hero.badge")}
                        </span>
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-white max-w-xl">
                        {t("hero.title")}
                    </h1>
                    <p className="mt-3 text-sm sm:text-base text-white/90 leading-relaxed max-w-md">
                        {t("hero.sub")}
                    </p>
                    <button
                        onClick={() =>
                            document.getElementById("productos")?.scrollIntoView({ behavior: "smooth" })
                        }
                        data-testid="hero-cta-primary"
                        className="group mt-5 w-full sm:w-auto bg-white hover:bg-cream text-bordeaux-deep text-xs sm:text-sm font-bold uppercase tracking-[0.14em] py-4 px-10 rounded-xl shadow-xl transition-colors flex items-center justify-center gap-2.5"
                    >
                        {t("hero.ctaPrimary")}
                        <ArrowRight
                            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                            strokeWidth={2.2}
                        />
                    </button>
                    <p className="mt-3 text-[11px] sm:text-xs text-white/75 tracking-wide text-center sm:text-left">
                        {t("hero.ctaSmall")}
                    </p>
                </div>
            </div>
        </section>
    );
};

const TrustStrip = () => {
    const { t } = useLang();
    return (
        <div className="relative z-10 -mt-7 px-2 sm:px-0">
            <div className="bg-[#FAF6F1] lg:bg-white rounded-t-3xl lg:rounded-none border-b border-stone-200">
                <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 grid grid-cols-4 py-5 sm:py-6 gap-1">
                    {t("trust").map((item, i) => {
                        const icons = [Truck, Heart, ShieldCheck, MapPin];
                        const Icon = icons[i];
                        return (
                            <div
                                key={i}
                                className="flex flex-col items-center gap-1.5 text-center px-0.5"
                            >
                                <Icon className="h-6 w-6 text-bordeaux shrink-0" strokeWidth={1.4} />
                                <span className="text-[10px] sm:text-xs font-medium text-stone-700 leading-tight">
                                    {item}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const ProductGrid = () => {
    const { t } = useLang();
    const ordered = ["romantic-pack-supreme", "surprise-box-balloons", "custom-teddy", "fresh-roses"]
        .map((id) => products.find((p) => p.id === id));
    return (
        <section id="productos" data-testid="product-grid" className="bg-cream">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
                <div className="text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl text-stone-900">
                        {t("products.title")}
                    </h2>
                    <p className="mt-2 text-sm text-stone-500">{t("products.sub")}</p>
                </div>
                <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                    {ordered.map((p, i) => (
                        <div key={p.id} className="h-full flex">
                            <ProductCard product={p} index={i} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const ReassuranceSection = () => {
    const { t } = useLang();
    const icons = [Truck, Heart, Gift, MapPin];
    const items = t("reassure.items") || [];
    return (
        <section data-testid="reassurance-section" className="bg-bordeaux-deep">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                <div className="text-center">
                    <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.25em] text-[#F2C4CE]">
                        {t("reassure.label")}
                    </p>
                    <h2 className="mt-2.5 font-serif text-2xl sm:text-3xl lg:text-4xl text-white leading-tight">
                        {t("reassure.title")}
                    </h2>
                    <p className="mt-2 text-sm sm:text-base text-[#F6DFE4] max-w-lg mx-auto">
                        {t("reassure.sub")}
                    </p>
                </div>
                <div className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
                    {items.map((it, i) => {
                        const Icon = icons[i % icons.length];
                        return (
                            <div
                                key={i}
                                data-testid={`reassurance-item-${i + 1}`}
                                className="bg-cream rounded-xl shadow-[0_12px_28px_-16px_rgba(0,0,0,0.55)] px-3 sm:px-5 py-5 flex flex-col items-center text-center gap-2"
                            >
                                <span className="h-14 w-14 rounded-full bg-bordeaux-light flex items-center justify-center">
                                    <Icon className="h-7 w-7 text-bordeaux" strokeWidth={1.7} />
                                </span>
                                <h3 className="text-sm sm:text-[15px] font-bold text-stone-900 leading-snug">
                                    {it.title}
                                </h3>
                                <p className="text-xs sm:text-[13px] text-stone-600 leading-snug">
                                    {it.text}
                                </p>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-8 text-center">
                    <Link
                        to="/regalo/pack-romantico"
                        data-testid="reassurance-cta"
                        className="group inline-flex items-center justify-center gap-2.5 bg-cream hover:bg-white text-bordeaux-deep text-xs sm:text-sm font-bold uppercase tracking-[0.14em] py-3.5 px-10 rounded-xl shadow-lg transition-colors"
                    >
                        {t("reassure.cta")}
                        <ArrowRight
                            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                            strokeWidth={2.2}
                        />
                    </Link>
                </div>
            </div>
        </section>
    );
};

const DeliverySection = () => {
    const { t } = useLang();
    const [zonesOpen, setZonesOpen] = useState(false);

    return (
        <section id="entrega" data-testid="delivery-info-section" className="bg-white border-y border-stone-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
                <div className="text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl text-stone-900">
                        {t("delivery.title")}
                    </h2>
                    <p className="mt-3 text-sm sm:text-base text-stone-600 leading-relaxed max-w-md mx-auto">
                        {t("delivery.sub")}
                    </p>
                    <button
                        onClick={() => setZonesOpen(!zonesOpen)}
                        data-testid="delivery-zones-toggle"
                        className="mt-5 inline-flex items-center gap-2 bg-bordeaux hover:bg-bordeaux-deep text-white text-sm font-semibold py-2.5 px-5 rounded-lg transition-colors"
                    >
                        {zonesOpen ? t("delivery.hideZonesBtn") : t("delivery.zonesBtn")}
                        <ChevronDown
                            className={`h-4 w-4 transition-transform duration-300 ${zonesOpen ? "rotate-180" : ""}`}
                            strokeWidth={2}
                        />
                    </button>
                    <AnimatePresence>
                        {zonesOpen && (
                            <motion.ul
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-xl mx-auto text-left"
                            >
                                {t("delivery.zones").map((z, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-stone-700">
                                        <MapPin className="h-3.5 w-3.5 text-bordeaux shrink-0" strokeWidth={2} />
                                        {z}
                                    </li>
                                ))}
                            </motion.ul>
                        )}
                    </AnimatePresence>
                </div>

                <div className="mt-10 md:mt-12">
                    <ReviewsCarousel />
                </div>
            </div>
        </section>
    );
};

const FaqSection = () => {
    const { t } = useLang();
    return (
        <section id="faq" className="bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
                <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 text-center">
                    {t("faq.title")}
                </h2>
                <div className="mt-8">
                    <Faq />
                </div>
            </div>
        </section>
    );
};

export default function Home() {
    return (
        <main>
            <Hero />
            <TrustStrip />
            <DeliveryMarquee />
            <ProductGrid />
            <ReassuranceSection />
            <DeliverySection />
            <FaqSection />
        </main>
    );
}
